import { useMemo } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter,
} from 'recharts'
import { usePortfolioAnalyticsBook } from '@/hooks/useDemoDataAccess'
import { riskTierRank } from '@/utils/dealTableSort'
import {
  CHART_TOOLTIP_PROPS, chartAxisProps, chartGridProps, chartPartOfWholeColor, chartRiskTierColor,
} from '@/config/chartTheme'

const RISK_TIER_LABEL = ['low', 'medium', 'high'] as const

const fmt = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(2)}B` : `$${v.toFixed(1)}M`
const pct = (v: number, d = 1) => `${(v * 100).toFixed(d)}%`

const LTV_BUCKET_DEFS = [
  { label: '<60%', min: 0, max: 0.60 },
  { label: '60-65%', min: 0.60, max: 0.65 },
  { label: '65-70%', min: 0.65, max: 0.70 },
  { label: '70-75%', min: 0.70, max: 0.75 },
  { label: '>75%', min: 0.75, max: 1.0 },
] as const

const LTV_BUCKETS = LTV_BUCKET_DEFS.map((bucket, index) => ({
  ...bucket,
  color: chartPartOfWholeColor(index, LTV_BUCKET_DEFS.length),
}))

const DECLINE_SCENARIOS = [5, 10, 15, 20]

export default function PortfolioLtvAnalysis() {
  const deals = usePortfolioAnalyticsBook().complete

  const distribution = useMemo(() => {
    return LTV_BUCKETS.map((bucket) => {
      const inBucket = deals.filter((d) => d.ltv >= bucket.min && d.ltv < bucket.max)
      const volume = inBucket.reduce((s, d) => s + d.loanAmount, 0)
      return { ...bucket, count: inBucket.length, volume }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const totalAum = deals.reduce((s, d) => s + d.loanAmount, 0)
  const wtdLtv = deals.reduce((s, d) => s + d.ltv * d.loanAmount, 0) / totalAum

  // Sensitivity table
  const sensitivity = useMemo(() => {
    return DECLINE_SCENARIOS.map((decline) => {
      const factor = 1 - decline / 100
      let totalStressedLtv = 0
      let breaches = 0
      deals.forEach((d) => {
        const origValue = d.propertyValue ?? d.loanAmount / (d.ltv || 0.70)
        const stressedValue = origValue * factor
        const stressedLtv = d.loanAmount / stressedValue
        totalStressedLtv += stressedLtv * d.loanAmount
        if (stressedLtv > 0.75) breaches++
      })
      return {
        scenario: `-${decline}%`,
        wtdLtv: totalStressedLtv / totalAum,
        breaches,
        dealCount: deals.length,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // LTV x DSCR x loan size — surfaces outlier risk concentration the
  // bucketed histogram above can't show (a handful of oversized, thin-margin
  // deals reads identically to many small ones in a bar chart).
  const scatterPoints = useMemo(() => {
    return deals.map((d) => {
      const ltv = d.ltv <= 1 ? d.ltv : d.ltv / 100
      return {
        id: d.id,
        property: d.property,
        ltv: ltv * 100,
        dscr: d.dscr,
        loanAmount: d.loanAmount,
        tier: RISK_TIER_LABEL[riskTierRank(d)],
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="metric-cell">
          <div className="metric-cell-label">Weighted Avg LTV</div>
          <div className={`metric-cell-value ${wtdLtv > 0.70 ? 'text-amber-400' : 'text-emerald-400'}`}>{pct(wtdLtv)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Total Portfolio AUM</div>
          <div className="metric-cell-value">{fmt(totalAum)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Deals &gt; 75% LTV</div>
          <div className={`metric-cell-value ${distribution[4].count > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            {distribution[4].count}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Distribution Histogram */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">LTV Distribution</span>
          </div>
          <div className="panel-body">
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distribution} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
                  <CartesianGrid {...chartGridProps()} />
                  <XAxis dataKey="label" {...chartAxisProps()} />
                  <YAxis {...chartAxisProps()} />
                  <Tooltip
                    {...CHART_TOOLTIP_PROPS}
                    formatter={(v: number, name: string) => [name === 'count' ? `${v} deals` : fmt(v), name === 'count' ? 'Deals' : 'Volume']}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {distribution.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Sensitivity Table */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">LTV Sensitivity (Value Decline)</span>
          </div>
          <div className="panel-body">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th className="text-right">Wtd Avg LTV</th>
                  <th className="text-right">Breaches (&gt;75%)</th>
                  <th className="text-right">Breach Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-stone-200 font-medium">Current</td>
                  <td className="text-right font-mono text-emerald-400">{pct(wtdLtv)}</td>
                  <td className="text-right font-mono">{distribution[4].count}</td>
                  <td className="text-right font-mono">{pct(distribution[4].count / deals.length)}</td>
                </tr>
                {sensitivity.map((s) => (
                  <tr key={s.scenario}>
                    <td className="text-stone-300">Value {s.scenario}</td>
                    <td className={`text-right font-mono ${s.wtdLtv > 0.75 ? 'text-red-400 font-bold' : s.wtdLtv > 0.70 ? 'text-amber-400' : 'text-stone-200'}`}>
                      {pct(s.wtdLtv)}
                    </td>
                    <td className={`text-right font-mono ${s.breaches > 0 ? 'text-red-400' : 'text-stone-200'}`}>
                      {s.breaches}
                    </td>
                    <td className="text-right font-mono text-stone-400">
                      {pct(s.breaches / s.dealCount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Risk Scatter */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">LTV vs. DSCR Risk (bubble = loan size)</span>
        </div>
        <div className="panel-body">
          <div key={deals.length} className="h-[260px] animate-chart-reveal">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                <CartesianGrid {...chartGridProps()} />
                <XAxis
                  dataKey="ltv"
                  type="number"
                  name="LTV"
                  unit="%"
                  domain={['dataMin - 2', 'dataMax + 2']}
                  {...chartAxisProps()}
                />
                <YAxis
                  dataKey="dscr"
                  type="number"
                  name="DSCR"
                  unit="x"
                  domain={['dataMin - 0.1', 'dataMax + 0.1']}
                  {...chartAxisProps()}
                />
                <ZAxis dataKey="loanAmount" range={[40, 400]} name="Loan amount" unit="M" />
                <Tooltip
                  {...CHART_TOOLTIP_PROPS}
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={(value: number, name: string) => {
                    if (name === 'LTV') return [`${value.toFixed(1)}%`, name]
                    if (name === 'DSCR') return [`${value.toFixed(2)}x`, name]
                    return [`$${value.toFixed(1)}M`, name]
                  }}
                  labelFormatter={(_label, payload) => {
                    const point = payload?.[0]?.payload as { property?: string } | undefined
                    return point?.property ?? ''
                  }}
                />
                <Scatter data={scatterPoints} fillOpacity={0.85}>
                  {scatterPoints.map((p) => (
                    <Cell key={p.id} fill={chartRiskTierColor(p.tier)} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex items-center gap-4 text-[10px] text-stone-500">
            {RISK_TIER_LABEL.map((tier) => (
              <span key={tier} className="flex items-center gap-1.5 capitalize">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: chartRiskTierColor(tier) }}
                />
                {tier} risk
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
